import { produce } from 'immer';

import {
  APP_MENU_TOGGLE,
  APP_NAVIGATION_TOGGLE,
  APP_STORE_RELOAD,
  AUTH_LOGIN,
  AUTH_LOGIN_SSO,
  AUTH_LOGOUT,
  AUTH_RESTORE_PASSWORD,
  FAQ_GET,
  FIRST_AUTH_LOGIN,
  LOADER_CLOSE,
  LOADER_OPEN,
  LOGO_GET,
  MAIL_SENT_RESET,
  MODAL_CLOSE,
  MODAL_OPEN,
  OTP_AUTH_LOGIN,
  SET_IS_ACTIVE,
  SSO_PROVIDER_LIST_GET,
  SUPPORT_MAIL_GET,
  VOCABULARY_GET,
} from '../actions';
import { getToken, setToken } from '../../api/token';
import storage from '../../api/storage';
import { SURVEY_SEND_ANSWERS } from '../actions/coursedetails';
import { PUBLIC_PAGE_DATA_GET } from '../actions/public';

const initialState = {
  loader: {
    instances: 0,
    isSpinning: true,
    fullPage: true,
  },
  authState: {
    isLogged: !!getToken(true)?.token,
    error: null,
    polling: false,
    mailSent: false,
  },
  supportMail: null,
  menuMobileOpen: false,
  notificationMenuMobileOpen: false,
  navigationMobile: false,
  appConfig: {
    homeHeroImage: null,
    logo: {},
    privacy_message: '',
    login_logo: null,
  },
  providerList: [],
  faq: {},
  vocabulary: {},
  tooltip: {},
  modal: {
    isOpen: false,
    id: undefined,
    payload: {},
  },
  completedSurveys: [],
  isActive: true,
};

const appReducer = (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case APP_STORE_RELOAD: {
        draft.authState.isLogged = !!getToken(true)?.token;
        break;
      }

      case APP_MENU_TOGGLE:
        draft.menuMobileOpen = !state.menuMobileOpen;
        break;

      case APP_NAVIGATION_TOGGLE:
        draft.menuMobileOpen = initialState.menuMobileOpen;
        draft.navigationMobile = !state.navigationMobile;
        break;

      case LOADER_OPEN: {
        const i = state.loader.instances > 0 ? state.loader.instances + 1 : 1;
        draft.loader.instances = i;
        draft.loader.isSpinning = i > 0;

        if (action.fullPage) draft.loader.fullPage = true;

        break;
      }
      case LOADER_CLOSE: {
        const i = state.loader.instances >= 1 ? state.loader.instances - 1 : 0;
        draft.loader.instances = i;
        draft.loader.isSpinning = i > 0;

        if (action.fullPage || i === 0) draft.loader.fullPage = false;

        break;
      }

      case FIRST_AUTH_LOGIN._SUCCESS: {
        const { message } = action.data;
        draft.message = message;
        break;
      }

      case AUTH_LOGIN._SUCCESS:
      case AUTH_LOGIN_SSO._SUCCESS:
      case OTP_AUTH_LOGIN._SUCCESS: {
        const { token } = action;
        setToken({ token });
        draft.authState.isLogged = true;
        draft.authState.error = initialState.authState.error;
        break;
      }

      case OTP_AUTH_LOGIN._ERROR: {
        draft.authState.error = action.response;
        break;
      }

      case AUTH_LOGOUT._SUCCESS:
        draft.authState.isLogged = false;
        draft.authState.error = initialState.authState.error;
        storage.clearAll();
        break;

      case AUTH_RESTORE_PASSWORD._SUCCESS:
        draft.authState.mailSent = true;
        break;
      case MAIL_SENT_RESET:
        draft.authState.mailSent = false;
        break;

      case AUTH_RESTORE_PASSWORD._ERROR: {
        const { data = {} } = action.response;

        if (data.message) {
          draft.authState.error = {
            ...state.authState.error,
            message: data.message,
          };
        } else {
          draft.authState.error = {
            ...state.authState.error,
            message: 'Unexpected error',
          };
        }

        draft.authState.polling = false;
        break;
      }

      case FIRST_AUTH_LOGIN._ERROR: {
        const { data = {} } = action.response;

        if (data.message) {
          draft.authState.error = {
            ...state.authState.error,
            message: data.message,
          };
        } else {
          draft.authState.error = {
            ...state.authState.error,
            message: 'Unexpected error',
          };
        }

        draft.authState.isLogged = false;
        storage.clearAll();
        break;
      }

      case FAQ_GET._SUCCESS: {
        const { data } = action;
        draft.faq = data;

        break;
      }

      case VOCABULARY_GET._SUCCESS: {
        const { vocabulary } = action;
        const newVocabulary = {};
        (vocabulary || []).forEach((t) => (newVocabulary[t?.key] = t.value));
        draft.vocabulary = newVocabulary;
        break;
      }

      case LOGO_GET._SUCCESS: {
        draft.appConfig.logo.src = action?.logo?.src;
        draft.appConfig.logo.alt = action?.logo?.alt;
        break;
      }

      case PUBLIC_PAGE_DATA_GET._SUCCESS: {
        draft.appConfig.privacy_message =
          action?.response?.login_privacy_text ?? '';
        draft.appConfig.login_logo = action?.response?.login_logo ?? null;
        break;
      }

      case SSO_PROVIDER_LIST_GET._SUCCESS: {
        const { response = {} } = action;
        draft.providerList = Object.keys(response).map((key, i) => ({
          ...response[key],
          value: i + 1,
          label: key,
        }));
        break;
      }

      case SUPPORT_MAIL_GET._SUCCESS: {
        draft.supportMail = action?.response?.help_desk_email_link;
        break;
      }

      case MODAL_OPEN: {
        draft.modal.isOpen = true;
        draft.modal.id = action.id;
        draft.modal.payload = action.payload ?? {};
        break;
      }

      case MODAL_CLOSE: {
        draft.modal = initialState.modal;
        break;
      }

      case SURVEY_SEND_ANSWERS._SUCCESS: {
        const id = action?.course_id;
        draft.completedSurveys = [...state.completedSurveys, id];
        break;
      }

      case SET_IS_ACTIVE: {
        draft.isActive = action?.action;
        break;
      }

      default:
        return state;
    }
    return draft;
  });

export default appReducer;
